<template>
  <div class="candidates-wrapper">
    <div>
      <div class="container col-lg-12 col-sm-12 candidates">
        <div v-if="isFetchingData">
          <v-skeleton-loader v-for="i in 15" :key="i" type="list-item-avatar" />
        </div>
        <div v-else-if="isNotEmpty" class="row">
          <div
            class="col-12 candidate-list"
            :class="{ 'col-md-4': showDetails, 'with-details': showDetails }"
          >
            <div class="candidates-toolbar">
              <DynamicSearchBar @search="onSearch" />
              <SelectMultipleButton
                showText
                :selectMultiple="selectMultiple"
                @onSelectMultiple="triggerCheckboxMode"
              />
            </div>
            <div
              :class="{ 'mt-4': index > 0 }"
              v-for="(applications, groupsKey, index) in applicationsToDisplay"
              :key="index"
            >
              <div class="status">
                <div class="d-flex">
                  <h3 class="text-capitalize">{{ groupsKey }}</h3>
                  <div class="status-count">
                    {{ applications.length }}
                  </div>
                </div>
                <PrimaryButton
                  v-if="selectMultiple"
                  :text="!isSelectedByStatus[groupsKey]"
                  :light="isSelectedByStatus[groupsKey]"
                  :fullWidth="isMobile"
                  @click.native="toggleSelectAll(groupsKey)"
                  shrink
                >
                  Select All
                </PrimaryButton>
              </div>
              <div
                v-for="(application, index) in applications"
                :key="index"
                @mouseleave.native="hovered = false"
                @mouseenter.native="hovered = true"
                @click="selectApplication(application)"
                :class="{
                  'worker-selected': isSelected(application),
                  'worker-hovered': hovered
                }"
                class="worker-wrapper"
              >
                <v-checkbox
                  class="checkbox-large-icon"
                  size="2rem"
                  v-if="selectMultiple"
                  :input-value="isSelected(application)"
                />
                <div class="icon-wrapper" v-else>
                  <v-icon>mdi-account-outline</v-icon>
                </div>
                <div class="pl-5">
                  {{ candidate(application) }}
                </div>
              </div>
            </div>
          </div>
          <CandidateDetailsDesktop
            :key="detailsKey"
            v-if="showDetails && !isMobile"
            class="col-12 col-md-8"
            :application="first(selectedApplications)"
          />
          <CandidateDetailsMobile
            :key="detailsKey"
            v-else-if="showDetails"
            :application="first(selectedApplications)"
            @close="selectedApplications = []"
          />
        </div>
        <EmptyStates
          v-else
          icon="applications-empty-state"
          description="No applications have been made yet"
        />
      </div>
      <slot name="action" />
      <BaseFooter v-if="selectedApplications.length && !isFetchingData">
        <ExpandedButtons>
          <div
            class="permissioned-container actions-bar"
            :class="{ vertical: isMobile }"
          >
            <WithdrawCandidatesAction
              :applicationsIds="selectedApplicationsIds"
              v-if="checkIfSelectedApplicationsArePermitted('withdraw')"
              @statusChanged="refreshView"
            />
            <RejectCandidatesAction
              v-if="checkIfSelectedApplicationsArePermitted('reject')"
              :applications="selectedApplications"
              @statusChanged="refreshView"
            />
            <EvaluationActions
              v-if="
                checkIfSelectedApplicationsArePermitted(
                  'actions.change-evaluation-status'
                )
              "
              :applications="selectedApplications"
              :evaluationStatuses="evaluationStatuses"
              @statusChanged="refreshView"
            />
            <AddAssessmentAction
              v-if="checkIfSelectedApplicationsArePermitted('add-assessment')"
              :applications="selectedApplications"
            />
            <ApproveCandidatesAction
              v-if="checkIfSelectedApplicationsArePermitted('approve')"
              :applicationsIds="selectedApplicationsIds"
              @statusChanged="refreshView"
            />
            <ShortlistCandidatesAction
              v-if="checkIfSelectedApplicationsArePermitted('shortlist')"
              :applicationsIds="selectedApplicationsIds"
              @statusChanged="refreshView"
            />
            <!--
          <CompareCandidatesAction
            v-if="selectedApplications.length > 1"
            :applications="selectedApplications"
          />
          -->
            <NewMessageAction
              v-if="
                checkIfSelectedApplicationsArePermitted(
                  'actions.message-candidate'
                )
              "
              :selectedCandidatesIds="selectedApplicationsIds"
              :selectedBookingId="currentBookingId"
            />
            <DownloadApplicationAction
              v-if="
                checkIfSelectedApplicationsArePermitted(
                  'actions.download-application'
                ) && !hideDownloadButton
              "
              :applicationsIds="selectedApplicationsIds"
            />
          </div>
        </ExpandedButtons>
      </BaseFooter>
    </div>
  </div>
</template>

<script>
import { filter, first, some, toLower, every, map, size, find } from "lodash";
import CandidateDetailsDesktop from "@/views/bookings/BookingDetails/CandidateDetails/CandidateDetailsDesktop";
import BaseFooter from "@/components/common/BaseFooter";
import WithdrawCandidatesAction from "@/views/bookings/components/actions/WithdrawCandidatesAction";
import RejectCandidatesAction from "@/views/bookings/components/actions/RejectCandidatesAction";
import AddAssessmentAction from "@/views/bookings/components/actions/AddAssessmentAction";
import ApproveCandidatesAction from "@/views/bookings/components/actions/ApproveCandidatesAction";
import ShortlistCandidatesAction from "@/views/bookings/components/actions/ShortlistCandidatesAction";
// import CompareCandidatesAction from "@/views/bookings/components/actions/CompareCandidatesAction";
import DownloadApplicationAction from "@/views/bookings/components/actions/DownloadApplicationAction";
import ExpandedButtons from "@/components/common/ExpandedButtons";
import EvaluationActions from "@/views/bookings/components/actions/EvaluationActions";
import CandidateDetailsMobile from "@/views/bookings/BookingDetails/CandidateDetails/CandidateDetailsMobile";
import DynamicSearchBar from "@/components/common/DynamicSearchBar";
import NewMessageAction from "@/views/conversations/actions/NewMessageAction";
import SelectMultipleButton from "@/components/common/SelectMultipleButton";
import { isPermissioned } from "@/utils/permissions";
import { createNamespacedHelpers } from "vuex";
import {
  BOOKINGS_NAMESPACE,
  FETCH_CURRENT_BOOKING_CANDIDATES,
  FETCH_EVALUATION_STATUSES
} from "@/store/modules/bookings/actions";
import {
  GET_CURRENT_BOOKING_CANDIDATES,
  IS_LOADING_BOOKING_CANDIDATES,
  GET_EVALUATION_STATUSES
} from "@/store/modules/bookings/getters";
import generateId from "uuid/v4";
import PrimaryButton from "@/components/common/Button/PrimaryButton";
import { getGroupedAndSortedApplications } from "@/utils/applications";
import EmptyStates from "@/components/common/EmptyStates";
import { getFullName, showCandidateName } from "@/utils/users";

const { mapActions, mapGetters } = createNamespacedHelpers(BOOKINGS_NAMESPACE);

export default {
  name: "Candidates",
  components: {
    CandidateDetailsMobile,
    ShortlistCandidatesAction,
    ApproveCandidatesAction,
    AddAssessmentAction,
    RejectCandidatesAction,
    WithdrawCandidatesAction,
    BaseFooter,
    CandidateDetailsDesktop,
    DynamicSearchBar,
    // CompareCandidatesAction,
    SelectMultipleButton,
    NewMessageAction,
    DownloadApplicationAction,
    EvaluationActions,
    PrimaryButton,
    EmptyStates,
    ExpandedButtons
  },
  data() {
    return {
      selectedApplications: [],
      selectedFilter: null,
      hovered: false,
      searchText: "",
      selectMultiple: false,
      isFetchingData: false,
      detailsKey: generateId(),
      isSelectedByStatus: {}
    };
  },
  async created() {
    this.fetchApplications({ onInit: true });
  },
  computed: {
    ...mapGetters({
      allApplications: GET_CURRENT_BOOKING_CANDIDATES,
      isLoadingBookingCandidates: IS_LOADING_BOOKING_CANDIDATES,
      evaluationStatuses: GET_EVALUATION_STATUSES
    }),
    externalSelectedApplication() {
      const { applicationId } = this.$route.query;
      if (applicationId) {
        return find(this.filteredApplications, { id: applicationId });
      }
      return null;
    },
    showDetails() {
      return this.selectedApplications.length === 1 && !this.selectMultiple;
    },
    applicationsToDisplay() {
      return getGroupedAndSortedApplications(
        this.searchedApplications,
        this.evaluationStatuses
      );
    },
    filteredApplications() {
      return filter(this.allApplications, "status");
    },
    searchedApplications() {
      const searchableAttributes = ["firstName", "lastName"];
      return filter(this.filteredApplications, application =>
        some(searchableAttributes, attribute =>
          toLower(application.user[attribute]).includes(
            toLower(this.searchText)
          )
        )
      );
    },
    applicationStatuses() {
      return this.searchedApplications
        .map(({ status }) => ({
          status
        }))
        .filter(Boolean);
    },
    currentBookingId() {
      return this.$route.params.id;
    },
    selectedApplicationsIds() {
      return map(this.selectedApplications, "id");
    },
    isNotEmpty() {
      return size(this.applicationsToDisplay);
    },
    hideDownloadButton() {
      return find(
        this.selectedApplications,
        ({ status }) => status === "draft"
      );
    }
  },
  methods: {
    ...mapActions({
      fetchCurrentBookingApplications: FETCH_CURRENT_BOOKING_CANDIDATES,
      fetchEvaluationStatuses: FETCH_EVALUATION_STATUSES
    }),
    onSearch(text) {
      this.searchText = text;
    },
    selectApplication(application) {
      const isAppSelected = this.isSelected(application);
      if (isAppSelected && this.selectMultiple) {
        this.selectedApplications = filter(
          this.selectedApplications,
          ({ id }) => id !== application.id
        );
      } else if (!isAppSelected) {
        this.selectedApplications = [
          ...(this.selectMultiple ? this.selectedApplications : []),
          application
        ].filter(Boolean);
        // refresh details component
        this.detailsKey = generateId();
      }
    },
    isSelected(application) {
      return some(this.selectedApplications, ({ id }) => {
        return id === application.id;
      });
    },
    checkIfSelectedApplicationsArePermitted(permission) {
      return every(this.selectedApplications, application => {
        return isPermissioned(`applications[${application.id}].${permission}`);
      });
    },
    triggerCheckboxMode() {
      if (this.selectMultiple) {
        this.selectedApplications = [];
        this.selectInitialApplication();
        this.initializeIsSelectedByStatus();
      }
      this.selectMultiple = !this.selectMultiple;
    },
    first,
    async fetchApplications({ onInit }) {
      this.isFetchingData = true;
      await this.fetchCurrentBookingApplications({
        bookingId: this.currentBookingId
      });
      this.selectInitialApplication(onInit);
      await this.fetchEvaluationStatuses(this.currentBookingId);
      this.initializeIsSelectedByStatus();
      this.isFetchingData = false;
    },
    selectInitialApplication(onInit) {
      if (!this.isMobile) {
        const isLink = onInit && this.externalSelectedApplication;
        const applicationToSelect = isLink
          ? this.externalSelectedApplication
          : first(this.filteredApplications);
        this.selectApplication(applicationToSelect);
      }
    },
    refreshView() {
      if (this.selectMultiple) {
        this.triggerCheckboxMode();
      }
      this.fetchApplications({ onInit: false });
    },
    async toggleSelectAll(status) {
      this.isSelectedByStatus[status] = !this.isSelectedByStatus[status];
      map(this.applicationsToDisplay[status], application => {
        if (this.isSelectedByStatus[status]) {
          if (!this.isSelected(application)) {
            this.selectedApplications.push(application);
          }
        } else {
          this.selectedApplications = filter(
            this.selectedApplications,
            ({ id }) => id !== application.id
          );
        }
      });
    },
    initializeIsSelectedByStatus() {
      map(
        this.applicationsToDisplay,
        (applications, status) => (this.isSelectedByStatus[status] = false)
      );
    },
    candidate(application) {
      return showCandidateName(application)
        ? getFullName(application.user)
        : `Candidate ${application.id}`;
    }
  },
  watch: {
    applicationsToDisplay() {
      // refresh application details on application update
      if (size(this.selectedApplications) === 1) {
        this.selectedApplications = filter(
          this.allApplications,
          application => {
            if (application.id === first(this.selectedApplications).id) {
              return application;
            }
          }
        );
      }
    }
  }
};
</script>

<style lang="scss"></style>
